import { FC, JSX, useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Select, Text, TextField } from "@radix-ui/themes";
import { countriesList } from "@/features/profile/constants/countries";
import { eligibleCountries } from "@/features/profile/constants/eligibleCountries";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { timestamp } from "@/api/firebase/firebase.config";
import { Timestamp } from "firebase/firestore";
import { RadixPhoneInput } from "@/components/RadixPhoneInput/RadixPhoneInput";
import Alert from "@/components/Alert/Alert";
import { Profile, ProfileOnboarding } from "@/models/Profile.model";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
} from "react-international-phone";

interface ProfileFormProps {
  initialValues: Profile | ProfileOnboarding;
  onSubmit: ((values: Profile) => void) | ((values: ProfileOnboarding) => void);
  loading: boolean;
  error?: { message?: string } | null;
  submitButtonText?: string;
}

const ProfileForm: FC<ProfileFormProps> = ({
  initialValues,
  onSubmit,
  loading,
  error,
  submitButtonText = "Submit",
}): JSX.Element => {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [values, setValues] = useState<Profile | ProfileOnboarding>(
    initialValues
  );

  const setFormValue = (
    targetName: string,
    value: string | boolean | Timestamp
  ) => {
    return setValues((prev) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setIsCalendarVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Flex direction="column" gap="4">
      <Flex gap="4">
        <Box width="50%">
          {/* First Name */}
          <Flex direction="column" gap="1">
            <Text as="label" size="2">
              First Name
            </Text>
            <TextField.Root
              size="3"
              type="text"
              value={values.firstName || ""}
              onChange={(e) => {
                setFormValue("firstName", e.target.value);
              }}
            />
          </Flex>
        </Box>
        {/* Last Name */}
        <Box width="50%">
          <Flex direction="column" gap="1">
            <Text as="label" size="2">
              Last Name
            </Text>
            <TextField.Root
              size="3"
              type="text"
              value={values.lastName || ""}
              onChange={(e) => {
                setFormValue("lastName", e.target.value);
              }}
            />
          </Flex>
        </Box>
      </Flex>

      {/* Nationality */}
      <Flex gap="4">
        <Box width="50%">
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                Nationality
              </Text>
            </Flex>
            <Select.Root
              defaultValue={values.nationality || countriesList.userCountryCode}
              value={values.nationality || ""}
              onValueChange={(e) => {
                setFormValue("nationality", e);
              }}
              size="3"
            >
              <Select.Trigger />
              <Select.Content>
                <Select.Group>
                  <Select.Label>Countries</Select.Label>
                  {defaultCountries.map((c) => {
                    const country = parseCountry(c);
                    return (
                      <Select.Item key={country.iso2} value={country.iso2}>
                        <Flex gap="3" align="center">
                          <FlagImage iso2={country.iso2} />
                          <Text size="2" weight="medium">
                            {country.name}
                          </Text>
                        </Flex>
                      </Select.Item>
                    );
                  })}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
        </Box>

        {/* Country of Residence */}
        <Box width="50%">
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                Country of Residence
              </Text>
            </Flex>
            <Select.Root
              defaultValue={
                values.countryOfResidence || eligibleCountries.userCountryCode
              }
              value={values.countryOfResidence || ""}
              onValueChange={(e) => {
                setFormValue("countryOfResidence", e);
              }}
              size="3"
            >
              <Select.Trigger />
              <Select.Content>
                <Select.Group>
                  <Select.Label>Where are you currently living?</Select.Label>

                  {defaultCountries.map((c) => {
                    const country = parseCountry(c);
                    return (
                      <Select.Item key={country.iso2} value={country.iso2}>
                        <Flex gap="3" align="center">
                          <FlagImage iso2={country.iso2} />
                          <Text size="2" weight="medium">
                            {country.name}
                          </Text>
                        </Flex>
                      </Select.Item>
                    );
                  })}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
        </Box>
      </Flex>

      <Flex gap="4" direction={{ initial: "column", lg: "row" }}>
        {/* Gender */}
        <Box width={{ initial: "100%", lg: "50%" }}>
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                Gender
              </Text>
            </Flex>
            <Select.Root
              onValueChange={(e) => {
                setFormValue("gender", e);
              }}
              value={values.gender || ""}
              size="3"
            >
              <Select.Trigger />
              <Select.Content>
                <Select.Group>
                  <Select.Label>What is your gender?</Select.Label>
                  <Select.Item value="male">Male</Select.Item>
                  <Select.Item value="female">Female</Select.Item>
                  <Select.Item value="other">Other</Select.Item>
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
        </Box>

        <Box width={{ initial: "100%", lg: "50%" }}>
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                Phone Number
              </Text>
            </Flex>
            <RadixPhoneInput
              value={values.phoneNumber || ""}
              onPhoneChange={(e) => setFormValue("phoneNumber", e)}
            />
          </Flex>
        </Box>
      </Flex>

      {/* Date of Birth */}
      <Flex direction="column" gap="1" position="relative">
        <Text as="label" size="2">
          Date of Birth
        </Text>
        <TextField.Root
          size="3"
          type="text"
          readOnly
          value={
            values.dateOfBirth
              ? values.dateOfBirth.toDate().toLocaleDateString("en-GB")
              : ""
          }
          onClick={() => setIsCalendarVisible(!isCalendarVisible)}
          placeholder="Select a date"
        />
        {isCalendarVisible && (
          <Box
            position="absolute"
            inset="100% auto auto 0"
            style={{ zIndex: 1001 }}
            ref={calendarRef}
          >
            <Calendar
              defaultView="month"
              onChange={(e) => {
                setIsCalendarVisible(false);
                setFormValue("dateOfBirth", timestamp.fromDate(e as Date));
              }}
            />
          </Box>
        )}
      </Flex>

      {error && error.message && <Alert>{error.message}</Alert>}

      <Box>
        <Button
          onClick={() => {
            if (!loading) {
              onSubmit(values as Profile);
            }
          }}
        >
          {loading ? "Processing..." : submitButtonText}
        </Button>
      </Box>
    </Flex>
  );
};

export default ProfileForm;
